import { useCallback, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import focusStyle from 'styles/utils/focusStyle'

type NestableClickableElement = HTMLAnchorElement | HTMLDivElement

interface Props extends Omit<React.ComponentProps<typeof Clickable>, 'ref'> {
  ref?: React.Ref<NestableClickableElement>
}

const HiddenClickable = styled(Clickable)`
  position: absolute;
  inset: 0;
  width: 100%; /* The inset properties should take care of this, but it doesn't work in Firefox for some reason */
  pointer-events: none;

  @supports not (selector(:has(*))) {
    &:focus-visible {
      ${focusStyle}
    }
  }
`

const Root = styled.div<Omit<Props, 'ref' | 'onClick'>>`
  position: relative;
  cursor: pointer;

  &.disabled {
    cursor: not-allowed;
  }

  &:has(${HiddenClickable}:focus-visible) {
    ${focusStyle}
  }
`

const interactableHTMLTags = new Set(['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT', 'DETAILS', 'SUMMARY', 'IFRAME', 'EMBED', 'AUDIO', 'VIDEO'])

export function didClickInteractableElement(clickTarget: HTMLElement, root: HTMLElement | null) {
  let current: HTMLElement | null = clickTarget
  while (current && current !== root) {
    if (interactableHTMLTags.has(current.tagName)) { return true }
    current = current.parentElement
  }
  return false
}

/**
 * A Clickable that can have other button elements nested inside it safely.
 * Also applies a default focus indicator.
 */
function NestableClickable(props: Props) {
  const {
    children,
    className,
    onClick,
    onFocus,
    onBlur,
    disabled,
    ref,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...rest
  } = props
  const rootRef = useRef<NestableClickableElement>(null)

  // use a callback ref so that we can forward the ref to the parent component
  const setRootRef = useCallback((element: NestableClickableElement | null) => {
    rootRef.current = element

    if (typeof ref === 'function') {
      ref(element)
    } else if (ref) {
      ref.current = element
    }
  }, [ref])

  const hiddenClickable = useRef<HTMLButtonElement>(null)

  const onRootClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()

    if (!didClickInteractableElement(e.target as HTMLElement, rootRef.current)) {
      hiddenClickable.current?.click()
    }
  }, [rootRef])

  const onHiddenClickableClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    onClick?.(e)
  }, [onClick])

  if ((rest.to || rest.href) && !disabled) {
    // The NestableClickable behaviour doesn't make sense for links, so fall back to ClickableLink
    return <ClickableLink {...props} ref={setRootRef} />
  }

  return <Root
    ref={setRootRef}
    className={clsx(className, { disabled })}
    {...rest}
    onClick={onRootClick}
  >
    <HiddenClickable
      data-nestable-clickable-overlay
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      onClick={onHiddenClickableClick}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
      ref={hiddenClickable}
    />
    {children}
  </Root>
}

NestableClickable.displayName = 'NestableClickable'

export default NestableClickable
